import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { EoPage, EoPageHeader } from './eoShared';

// Static staff guide. The design handoff included SQL/code blocks; the
// audience is inside sales / ops, so those are rewritten in plain language.
export const OnboardingGuidePage: React.FC = () => (
  <EoPage>
    <div style={{ maxWidth: 760 }}>
      <EoPageHeader
        title="Edge Onboard — Staff Guide"
        sub="How onboarding works, and when to use each action."
      />

      <div className="eo-prose">
        <h2>What is a Go-Live?</h2>
        <p>
          A <strong>Go-Live</strong> is a scheduled install at one Location, assigned to one
          customer user. It carries two independent statuses: a <strong>state</strong> (Not Started
          → In Progress → Complete) that tracks the customer's on-site work, and a{' '}
          <strong>review status</strong> (Not Started → Under Review → Complete) that tracks your
          review of what they submitted. The customer side is typically a one-shot, ~15-minute
          session on their phone; you review over the following hours or days.
        </p>

        <h2>Setting one up</h2>
        <p>
          1. Make sure the customer user exists on the{' '}
          <RouterLink className="edge-link" to="/onboarding/users">Users page</RouterLink> — they
          sign in with their email address and a starter password, and are asked to set their own
          password the first time they sign in. (Accounts migrated from the old onboarding app can
          keep signing in with their old username.)
        </p>
        <p>
          2. A go-live can only be created on a Location with a linked <strong>HQ Account</strong>.
          Unlinked locations show up disabled in the Create dialog — set the HexaHQ Account ID on
          the location record (main{' '}
          <RouterLink className="edge-link" to="/locations">Locations page</RouterLink>) and it
          becomes selectable.
        </p>
        <p>
          3. Load <strong>tag slips</strong> from the{' '}
          <RouterLink className="edge-link" to="/onboarding/tag_slips">Tag Slips page</RouterLink>{' '}
          before the install — scan the tags straight into the box (or paste them in) and the system
          numbers the slips automatically. We usually send more tags than the customer needs;
          spares are normal and never block anything.
        </p>

        <h2>How slip assignment works</h2>
        <p>
          When the customer submits a product for <em>N</em> units, the system hands out the next{' '}
          <em>N</em> available slips and marks them <strong>Pending</strong>. The customer sticks
          those exact slips on the units and confirms, which moves them to{' '}
          <strong>Assigned</strong>. It doesn't matter which slip ends up on which unit — any slip
          works on any unit of that product. Assignment is first-come, first-served and stays
          correct even if two people submit at the same time.
        </p>

        <h2>Reviewing submissions</h2>
        <p>
          Each submission shows the customer's photo (click it to zoom), quantity, restocking
          preference, and notes. <strong>Assigning a product marks the submission Reviewed</strong>{' '}
          in one step; use <strong>Mark reviewed</strong> when there's nothing to assign. Neither
          works while the submission's slips are still Pending on the customer's phone. Reviews are
          recorded under your work email, so it's always clear who signed off.
        </p>

        <h2>Completing &amp; reopening</h2>
        <p>
          <strong>Complete Go-Live</strong> is blocked while any slip is Pending — the customer has
          unfinished tagging in flight — and until at least one product has actually been tagged.
          Leftover <em>Available</em> slips are fine; extras never block completion. Completing updates the location's onboarding status everywhere else in
          Edge Admin. If the customer needs to add more products later, <strong>Reopen</strong> the
          go-live — nothing is lost.
        </p>

        <h2>Who can do all this</h2>
        <p>
          Anyone signed into Edge Admin with a Hexa Google account. Customers never see these pages
          — they only see their own onboarding flow on their phone.
        </p>
      </div>
    </div>
  </EoPage>
);
